import React, { useMemo, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Download, Trash2, Tag, Eye, EyeOff, FileText, Wrench, Search,
} from "lucide-react";

const Badge = ({ color, children, testId }) => {
  const c = {
    rojo: "bg-rose-100 text-rose-700 border-rose-200",
    ambar: "bg-amber-50 text-amber-800 border-amber-200",
    gris: "bg-slate-100 text-slate-500 border-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
  }[color] || "bg-slate-100 text-slate-500 border-slate-200";
  return (
    <span data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${c}`}>
      {children}
    </span>
  );
};

const fmtFecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-CR") : "—");

const FILTROS = [
  { value: "con_catalogo", label: "Con catálogo" },
  { value: "todos", label: "Todos" },
  { value: "sin_catalogo", label: "Sin catálogo" },
  { value: "sin_frt", label: "Sin FRT" },
  { value: "duplicados", label: "Duplicados" },
];

export const CatalogosPorModelo = ({ modelos, lotes, frtResumen, onEliminarLote,
                                     onEliminarFrt, onAsignarLote, onAsignarFrt, onDescargarLote }) => {
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("con_catalogo");
  const [abiertos, setAbiertos] = useState({});
  const [frtVisto, setFrtVisto] = useState({});   // modelo_texto -> ops[]|null (cargando)

  const grupos = useMemo(() => {
    const ordenados = [...modelos].sort((a, b) => {
      const na = `${a.marca || ""} ${a.modelo || ""}`.trim().toLowerCase();
      const nb = `${b.marca || ""} ${b.modelo || ""}`.trim().toLowerCase();
      if (na !== nb) return na.localeCompare(nb);
      return (a.anio_inicio || 0) - (b.anio_inicio || 0);
    }).map((m) => ({
      key: m.id,
      titulo: m.nombre_mostrar,
      modelo: m,
      catalogos: lotes.filter((l) => (l.modelo_ids || []).includes(m.id)),
      frts: frtResumen.filter((f) => (f.modelo_ids || []).includes(m.id)),
    }));
    const lotesSin = lotes.filter((l) => !(l.modelo_ids || []).length);
    const frtSin = frtResumen.filter((f) => !(f.modelo_ids || []).length);
    if (lotesSin.length || frtSin.length) {
      ordenados.push({ key: "__sin__", titulo: "Sin modelo asignado", modelo: null,
        catalogos: lotesSin, frts: frtSin });
    }
    return ordenados;
  }, [modelos, lotes, frtResumen]);

  const filtrados = useMemo(() => grupos.filter((g) => {
    if (busca && !g.titulo.toLowerCase().includes(busca.trim().toLowerCase())) return false;
    if (filtro === "con_catalogo" && g.catalogos.length === 0) return false;
    if (filtro === "sin_catalogo" && g.catalogos.length > 0) return false;
    if (filtro === "sin_frt" && g.frts.length > 0) return false;
    if (filtro === "duplicados" && g.catalogos.length < 2) return false;
    return true;
  }), [grupos, busca, filtro]);

  const buscando = busca.trim().length > 0;
  const estaAbierto = (key) => buscando || !!abiertos[key];

  const verFrt = async (mt) => {
    if (frtVisto[mt] !== undefined) {
      setFrtVisto((p) => { const n = { ...p }; delete n[mt]; return n; });
      return;
    }
    setFrtVisto((p) => ({ ...p, [mt]: null }));
    try {
      const { data } = await api.get("/frt/operaciones", { params: { modelo_texto: mt } });
      setFrtVisto((p) => ({ ...p, [mt]: data }));
    } catch (e) { toast.error(errorMsg(e)); setFrtVisto((p) => { const n = { ...p }; delete n[mt]; return n; }); }
  };

  return (
    <div data-testid="catalogos-por-modelo">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1 min-w-0">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="catref-buscar-modelo" value={busca}
            onChange={(e) => setBusca(e.target.value)} placeholder="Buscar modelo..."
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-400" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTROS.map((f) => (
            <button key={f.value} data-testid={`catref-filtro-${f.value}`}
              onClick={() => setFiltro(f.value)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                filtro === f.value ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtrados.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400" data-testid="catref-grupos-vacio">
          No hay modelos que coincidan con el filtro.
        </p>
      )}

      <div className="space-y-2">
        {filtrados.map((g, gi) => {
          const sinCat = g.catalogos.length === 0;
          const sinFrt = g.frts.length === 0;
          const duplicado = g.catalogos.length >= 2;
          const abierto = estaAbierto(g.key);
          return (
            <div key={g.key} data-testid={`catref-grupo-${gi}`}
              className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <button type="button" data-testid={`catref-grupo-toggle-${gi}`}
                onClick={() => setAbiertos((p) => ({ ...p, [g.key]: !p[g.key] }))}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50">
                {abierto ? <ChevronDown size={16} className="shrink-0 text-slate-400" />
                  : <ChevronRight size={16} className="shrink-0 text-slate-400" />}
                <span className={`font-semibold truncate ${g.modelo ? "text-slate-800" : "text-amber-800"}`}>
                  {g.titulo}
                </span>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                  {duplicado && <Badge color="rojo" testId={`catref-badge-duplicado-${gi}`}>Duplicado</Badge>}
                  {sinCat ? <Badge color="ambar" testId={`catref-badge-sincat-${gi}`}>Sin catálogo</Badge>
                    : <Badge color="indigo"><FileText size={11} /> {g.catalogos.length} catálogo{g.catalogos.length > 1 ? "s" : ""}</Badge>}
                  {sinFrt ? <Badge color="ambar" testId={`catref-badge-sinfrt-${gi}`}>Sin FRT</Badge>
                    : <Badge color="teal"><Wrench size={11} /> FRT</Badge>}
                </div>
              </button>

              {abierto && (
                <div className="border-t border-slate-100 px-3 py-3 space-y-3" data-testid={`catref-grupo-body-${gi}`}>
                  {/* Catálogos */}
                  <div>
                    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Catálogo</p>
                    {sinCat && <Badge color="gris">Sin catálogo</Badge>}
                    <div className="space-y-1.5">
                      {g.catalogos.map((l, li) => (
                        <div key={l.id} data-testid={`catref-lote-${gi}-${li}`}
                          className="flex flex-col gap-2 rounded-md bg-slate-50 p-2 sm:flex-row sm:items-center">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <FileText size={14} className="shrink-0 text-indigo-500" />
                              <span className="truncate font-medium text-slate-800">{l.nombre_archivo}</span>
                              {duplicado && <Badge color="rojo">Duplicado</Badge>}
                            </div>
                            <p className="mt-0.5 text-xs text-slate-500">
                              {l.filas} línea{l.filas === 1 ? "" : "s"} · {fmtFecha(l.creado_en)}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <button data-testid={`catref-lote-descargar-${gi}-${li}`} title="Descargar CSV"
                              onClick={() => onDescargarLote(l)}
                              className="inline-flex h-7 items-center gap-1 rounded-full bg-indigo-50 px-2 text-xs font-medium text-indigo-600 hover:bg-indigo-100">
                              <Download size={13} /> Descargar
                            </button>
                            <button data-testid={`catref-lote-asignar-${gi}-${li}`} title="Asignar / cambiar modelo"
                              onClick={() => onAsignarLote(l)}
                              className="inline-flex h-7 items-center gap-1 rounded-full bg-slate-100 px-2 text-xs font-medium text-slate-600 hover:bg-slate-200">
                              <Tag size={13} /> {(l.modelo_ids || []).length === 0 ? "Asignar" : "Cambiar"}
                            </button>
                            <button data-testid={`catref-lote-eliminar-${gi}-${li}`} title="Eliminar lote"
                              onClick={() => onEliminarLote(l)}
                              className="flex h-7 w-7 items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* FRT */}
                  <div>
                    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">FRT (mano de obra)</p>
                    {sinFrt && <Badge color="gris">Sin FRT</Badge>}
                    <div className="space-y-1.5">
                      {g.frts.map((m, fi) => {
                        const ops = frtVisto[m.modelo_texto];
                        const visto = m.modelo_texto in frtVisto;
                        return (
                          <div key={m.modelo_texto || fi} data-testid={`catref-frt-${gi}-${fi}`}
                            className="rounded-md bg-amber-50/50 p-2">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <Wrench size={14} className="shrink-0 text-amber-600" />
                                  <span className="truncate font-medium text-slate-800">{m.modelo_texto || "(sin texto)"}</span>
                                </div>
                                <p className="mt-0.5 text-xs text-slate-500">{m.operaciones} operaci{m.operaciones === 1 ? "ón" : "ones"}</p>
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <button data-testid={`catref-frt-ver-${gi}-${fi}`} title="Ver operaciones"
                                  onClick={() => verFrt(m.modelo_texto || "")}
                                  className="inline-flex h-7 items-center gap-1 rounded-full bg-amber-100 px-2 text-xs font-medium text-amber-700 hover:bg-amber-200">
                                  {visto ? <EyeOff size={13} /> : <Eye size={13} />} Ver
                                </button>
                                <button data-testid={`catref-frt-asignar-${gi}-${fi}`} title="Asignar modelos"
                                  onClick={() => onAsignarFrt(m)}
                                  className="inline-flex h-7 items-center gap-1 rounded-full bg-slate-100 px-2 text-xs font-medium text-slate-600 hover:bg-slate-200">
                                  <Tag size={13} /> Asignar
                                </button>
                                <button data-testid={`catref-frt-eliminar-${gi}-${fi}`} title="Eliminar FRT"
                                  onClick={() => onEliminarFrt(m)}
                                  className="flex h-7 w-7 items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            {visto && (
                              <div className="mt-2 overflow-x-auto" data-testid={`catref-frt-ops-${gi}-${fi}`}>
                                {ops === null ? (
                                  <p className="py-2 text-xs text-slate-400">Cargando operaciones...</p>
                                ) : ops.length === 0 ? (
                                  <p className="py-2 text-xs text-slate-400">Sin operaciones.</p>
                                ) : (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-amber-200 text-left uppercase text-slate-400">
                                        <th className="py-1 pr-2 font-semibold">Sección</th>
                                        <th className="py-1 pr-2 font-semibold">Operación</th>
                                        <th className="py-1 pr-2 font-semibold">Alcance</th>
                                        <th className="py-1 pr-2 font-semibold text-right">FRT</th>
                                        <th className="py-1 font-semibold">Código</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ops.map((o, oi) => (
                                        <tr key={oi} className="border-b border-amber-100/70">
                                          <td className="py-1 pr-2 text-slate-600">{o.seccion || "—"}</td>
                                          <td className="py-1 pr-2 text-slate-700">{o.operacion}</td>
                                          <td className="py-1 pr-2 text-slate-500">{o.alcance || "—"}</td>
                                          <td className="py-1 pr-2 text-right text-slate-700">{o.frt_horas}</td>
                                          <td className="py-1 text-slate-500">{o.codigo_parte || "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
